import { isNotNullOrUndefined } from '../utility/null-safety.js';
import { SerializationException } from '../exceptions/serialization-exception.js';

// When you see this type, just use the name of the enum.
export type EnumType = Record<string, string | number>;

function isEnumValue<Enum extends EnumType>(
    value: unknown,
    enumType: Enum,
): value is Enum[keyof Enum] {
  return Object.values(enumType).includes(value as Enum[keyof Enum]);
}

export function toEnumFromString<Enum extends EnumType>(
    value: string | null | undefined,
    enumType: Enum,
    defValue: Enum[keyof Enum] | null = null,
): Enum[keyof Enum] {
  return toEnumFrom(value, enumType, defValue);
}

export function toEnumFromInt<Enum extends EnumType>(
    value: number | null | undefined,
    enumType: Enum,
    defValue: Enum[keyof Enum] | null = null,
): Enum[keyof Enum] {
  return toEnumFrom(value, enumType, defValue);
}

export function toEnumFromStringOrNull<Enum extends EnumType>(
    value: string | null | undefined,
    enumType: Enum,
    defValue: Enum[keyof Enum] | null = null,
): Enum[keyof Enum] | null {
  try {
    return toEnumFromString(value, enumType, defValue);
  }
  catch{
    return null;
  }
}

export function toEnumFromStringOrNullOrUndefined<Enum extends EnumType>(
    value: string | null | undefined,
    enumType: Enum,
    defValue: Enum[keyof Enum] | null = null,
): Enum[keyof Enum] | null | undefined {
  if(value === undefined) return undefined;
  if(value === null) return null;
  return toEnumFromStringOrNull(value, enumType, defValue);
}

export function toEnumFromIntOrNull<Enum extends EnumType>(
    value: number | null | undefined,
    enumType: Enum,
    defValue: Enum[keyof Enum] | null = null,
): Enum[keyof Enum] | null {
  try {
    return toEnumFromInt(value, enumType, defValue);
  }
  catch{
    return null;
  }
}

function toEnumFrom<Enum extends EnumType>(
    value: string | number | null | undefined,
    enumType: Enum, defValue: Enum[keyof Enum] | null,
): Enum[keyof Enum] {
  if(isNotNullOrUndefined(value)) {
    if(isEnumValue(value, enumType)) {
      return value;
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    throw new SerializationException(`Failed to convert value to enum: ${value}`);
  }

  if(isNotNullOrUndefined(defValue)) {
    return defValue;
  }

  throw new SerializationException(`Failed to convert value to enum: ${value}`);
}

export function fromEnumAsInt<T>(value: T): number {
  if(typeof value === 'number') {
    return value;
  }

  throw new SerializationException(`Failed to convert enum value to number: ${value}`);
}

export function fromEnumAsString<T>(value: T): string {
  if(typeof value === 'string') {
    return value;
  }

  throw new SerializationException(`Failed to convert enum value to string: ${value}`);
}

export function fromEnumAsIntOrNull<T>(value: T | null): number | null {
  try {
    return fromEnumAsInt(value);
  }
  catch{
    return null;
  }
}

export function fromEnumAsStringOrNull<T>(value: T | null): string | null {
  try {
    return fromEnumAsString(value);
  }
  catch{
    return null;
  }
}

export function findValidEnumValuesFromList<Enum extends EnumType>(values: unknown[], enumType: Enum): Enum[keyof Enum][] {
  const result: Enum[keyof Enum][] = [];
  for(const value of values) {
    if(typeof value === 'number') {
      const enumValue = toEnumFromIntOrNull(value, enumType);
      if(isNotNullOrUndefined(enumValue)) {
        result.push(enumValue);
      }
    }
    else if(typeof value === 'string') {
      const enumValue = toEnumFromStringOrNull(value, enumType);
      if(isNotNullOrUndefined(enumValue)) {
        result.push(enumValue);
      }
    }
    else {
      throw new SerializationException(
          `Can not convert value: {${value}} of type: {${typeof value}} to enum: {${enumType}}`
      );
    }
  }

  return result;
}
